import type { OverlayProps } from "@dropbox/dig-components/overlay";
import { Overlay } from "@dropbox/dig-components/overlay";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import { getSourceRowParts } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import {
  useOverlayMargin,
  type OverlayMargin,
} from "@mirage/shared/hooks/useOverlayMargin";
import classNames from "classnames";
import { memo, useLayoutEffect, useRef, useState } from "react";
import { useDelayedHover } from "../ConversationAssistant/hooks/useDelayedHover";
import { AnswerCitationCard } from "./AnswerCitationCard";
import styles from "./AnswerCitationCardsInOverlay.module.css";

const OVERLAY_WIDTH_REGULAR = 359;
const OVERLAY_WIDTH_LIST_VIEW = 325;

interface AnswerCitationCardsInOverlayProps {
  placement: OverlayProps["placement"];
  sources: ChatSource[];
  refComponent: React.ReactNode;
  conversationId?: string;
  searchSessionId?: string;
  logComposeEvent?: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  assistRequestId?: string;
  listView?: boolean;
}
export const AnswerCitationCardsInOverlay = memo(
  ({
    placement,
    sources,
    refComponent,
    conversationId,
    searchSessionId,
    logComposeEvent,
    assistRequestId,
    listView,
  }: AnswerCitationCardsInOverlayProps) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

    const {
      isHovered,
      handlers: { onMouseEnter, onMouseLeave },
    } = useDelayedHover({
      onHoverStart: () => {
        if (logComposeEvent) {
          logComposeEvent(
            PAP_Click_DashChatAction({
              featureLine: "assist",
              dashChatAction: "hover_sources",
              conversationId,
            }),
          );
        }
      },
    });

    useLayoutEffect(() => {
      if (anchorRef.current) {
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const availableSpaceBelow = viewportHeight - anchorRect.bottom;
        const availableSpaceAbove = anchorRect.top;

        // Set max height based on available space
        setMaxHeight(
          Math.max(availableSpaceBelow, availableSpaceAbove) - 30, // Add padding
        );
      }
    }, [isHovered]);

    // Ensure card is at least 16px from right and left edges of screen
    const overlayWidth = listView
      ? OVERLAY_WIDTH_LIST_VIEW
      : OVERLAY_WIDTH_REGULAR;
    const overlayMargin = useOverlayMargin({
      anchorRef,
      overlayWidth,
      isHovered,
    });

    // Filter out sources that can't be rendered (including channel fallback)
    const validSources = sources.filter(
      (source) => getSourceRowParts(source) !== undefined,
    );

    return (
      <div
        className={classNames(styles.AnswerCitationCardsInOverlayContainer, {
          [styles.AnswerCitationCardsInOverlayFullWidth]: listView,
        })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div ref={anchorRef}>{refComponent}</div>
        {isHovered && (
          <Overlay
            anchorRef={anchorRef}
            placement={placement}
            auto={true}
            className={styles.AnswerCitationCardsInOverlayContent}
            positioningStrategy="fixed"
          >
            <OverlayWrapper
              maxHeight={maxHeight}
              overlayMargin={overlayMargin}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              {validSources.map((source, i) => (
                <div
                  className={classNames(
                    styles.AnswerCitationCardsInOverlayCard,
                    {
                      [styles.AnswerCitationCardsInOverlayCardListView]:
                        listView,
                    },
                  )}
                  key={i}
                >
                  {i == 0 ? undefined : (
                    <div
                      className={classNames(
                        styles.AnswerCitationCardsInOverlayCardDivider,
                        {
                          [styles.AnswerCitationCardsInOverlayCardDividerListView]:
                            listView,
                        },
                      )}
                    />
                  )}
                  <AnswerCitationCard
                    source={source}
                    conversationId={conversationId}
                    searchSessionId={searchSessionId}
                    assistRequestId={assistRequestId}
                    listItem={listView}
                    config={{
                      time: {
                        timeAgo: {
                          abbreviateTime: true,
                          abbreviateDate: false,
                        },
                        hidePrefix: listView,
                      },
                      icon: {
                        hasBackground: false,
                        size: "small",
                      },
                      subtitle: {
                        hideServiceName: true,
                        hideSizeOrMtime: false,
                        fallbackToServiceName: true,
                      },
                      dynamicTitleHeight: true,
                      removeBorder: true,
                    }}
                    logComposeEvent={logComposeEvent}
                  />
                </div>
              ))}
            </OverlayWrapper>
          </Overlay>
        )}
      </div>
    );
  },
);
AnswerCitationCardsInOverlay.displayName = "AnswerCitationCardsInOverlay";

export interface OverlayWrapperProps {
  maxHeight?: number;
  overlayMargin?: OverlayMargin;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  children: React.ReactNode;
}

export const OverlayWrapper = ({
  maxHeight,
  overlayMargin,
  onMouseEnter,
  onMouseLeave,
  children,
}: OverlayWrapperProps) => (
  <div
    data-testid="overlay-wrapper"
    className={styles.AnswerCitationCardsInOverlayWrapper}
    style={{
      ...(maxHeight != null ? { maxHeight: `${maxHeight}px` } : {}),
      ...(overlayMargin || {}),
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </div>
);
